import React, { useState, useEffect, useCallback } from 'react';
import { getDepositAddress, getDeposits } from '../services/api';
import QRCode from 'react-qr-code';
import { ClipboardDocumentIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Deposit = () => {
  const [depositAddress, setDepositAddress] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState('bsc');

  const networks = [
    { id: 'bsc', name: 'BSC (BEP-20)', asset: 'USDT', chain: 'bsc', minAmount: 10 },
    { id: 'bitcoin', name: 'Bitcoin', asset: 'BTC', chain: 'bitcoin', minAmount: 0.001 },
  ];

  const fetchDepositData = useCallback(
    async (showRefreshToast = false) => {
      try {
        const addressRes = await getDepositAddress(selectedNetwork);
        const depositsRes = await getDeposits();

        if (addressRes.data.success) {
          setDepositAddress(addressRes.data);
        }

        if (depositsRes.data.success) {
          setDeposits(depositsRes.data.deposits);
        }

        if (showRefreshToast) {
          toast.success('Deposit information refreshed');
        }
      } catch (error) {
        if (error.response?.status === 403) {
          setDepositAddress(null);
        } else {
          console.error('Deposit page error:', error);
          if (showRefreshToast) {
            toast.error('Failed to refresh deposit information');
          }
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [selectedNetwork]
  );

  useEffect(() => {
    setLoading(true);
    fetchDepositData(false);
  }, [fetchDepositData, selectedNetwork]);

  const handleNetworkChange = (networkId) => {
    setSelectedNetwork(networkId);
    setDepositAddress(null);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDepositData(true);
  };

  const copyToClipboard = () => {
    if (depositAddress?.deposit_address) {
      navigator.clipboard.writeText(depositAddress.deposit_address);
      toast.success('Address copied to clipboard');
    }
  };

  const currentNetwork = networks.find((n) => n.id === selectedNetwork);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-gray-400">Loading deposit information...</div>
      </div>
    );
  }

  if (!depositAddress) {
    return (
      <div className="dashboard-page fade-in">
        <section className="card dashboard-section">
          <div className="dashboard-section-body">
            <div className="styled-empty-state deposit-kyc-state">
              <div className="empty-state-icon deposit-warning-icon">⚠️</div>
              <p className="styled-empty-title">KYC Required</p>
              <p className="styled-empty-subtitle">
                Please complete KYC verification before making a deposit.
              </p>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="btn-secondary mt-4 inline-flex items-center gap-2"
              >
                <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Checking...' : 'Check KYC Status'}
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="dashboard-page fade-in">
      <div className="dashboard-head deposit-head">
        <div>
          <h1 className="dashboard-page-title">Deposit Funds</h1>
          <p className="dashboard-section-subtitle">Add funds to your account securely</p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-secondary text-sm py-2 px-4 inline-flex items-center gap-2"
        >
          <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <section className="card dashboard-section">
        <div className="dashboard-section-body">
          <div className="deposit-network-tabs">
            {networks.map((network) => (
              <button
                key={network.id}
                onClick={() => handleNetworkChange(network.id)}
                className={`deposit-network-tab ${selectedNetwork === network.id ? 'active' : ''}`}
              >
                {network.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">
            Send {currentNetwork.asset} ({currentNetwork.name})
          </h2>
          <p className="dashboard-section-subtitle">
            Use the address below and confirm the network before sending
          </p>
        </div>

        <div className="dashboard-section-body">
          <div className="deposit-grid">
            <div className="deposit-qr-card">
              <div className="qr-container mx-auto">
                <QRCode value={depositAddress.deposit_address} size={160} />
              </div>
              <p className="deposit-qr-caption">Scan with your wallet</p>
            </div>

            <div className="deposit-info-wrap">
              <div className="deposit-address-card">
                <label className="deposit-label">Deposit Address</label>
                <div className="deposit-address-row">
                  <input
                    type="text"
                    value={depositAddress.deposit_address}
                    readOnly
                    className="input-dark flex-1 font-mono text-xs"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="btn-secondary p-2 deposit-copy-btn"
                    title="Copy address"
                    type="button"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="deposit-meta-card">
                <p className="deposit-meta-item">
                  <span className="deposit-meta-label">Network:</span>{' '}
                  {depositAddress.network || currentNetwork.name}
                </p>
                <p className="deposit-meta-item">
                  <span className="deposit-meta-label">Asset:</span>{' '}
                  {depositAddress.asset || currentNetwork.asset}
                </p>
                <p className="deposit-meta-warning">
                  ⚠️ Send only {currentNetwork.asset} on {currentNetwork.name} to this address
                </p>
                <p className="deposit-meta-min">
                  Minimum deposit: {currentNetwork.minAmount} {currentNetwork.asset}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Deposit History</h2>
          <p className="dashboard-section-subtitle">Your recent deposit records</p>
        </div>

        <div className="dashboard-section-body">
          {deposits.length === 0 ? (
            <div className="styled-empty-state">
              <p className="styled-empty-title">No deposits yet</p>
              <p className="styled-empty-subtitle">Your deposit history will appear here</p>
            </div>
          ) : (
            <div className="market-table-wrap">
              <table className="table-dark w-full">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>Asset</th>
                    <th>TX Hash</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((deposit) => (
                    <tr key={deposit.id}>
                      <td className="font-medium">{parseFloat(deposit.amount_usdt).toFixed(2)}</td>
                      <td className="text-xs">{deposit.asset_symbol || 'USDT'}</td>
                      <td className="font-mono text-xs">
                        {deposit.tx_hash?.slice(0, 10)}...{deposit.tx_hash?.slice(-8)}
                      </td>
                      <td>
                        <span
                          className={
                            deposit.status === 'credited'
                              ? 'badge badge-success'
                              : deposit.status === 'pending'
                              ? 'badge badge-warning'
                              : 'badge badge-info'
                          }
                        >
                          {deposit.status}
                        </span>
                      </td>
                      <td className="text-gray-400 text-xs">
                        {new Date(deposit.detected_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Deposit;